import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { ChevronLeft } from 'lucide-react';
import { setPageTitle } from '../utils/pageTitle';

const CookiePolicy: React.FC = () => {
  useEffect(() => {
    setPageTitle('Cookie Policy');
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  // Function to handle anchor links
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back button */}
        <div className="mb-6">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Dashboard
          </Link>
        </div>

        <Card className="p-6 md:p-8 shadow-lg">
          {/* Header */}
          <div className="mb-8 border-b pb-6 dark:border-gray-700">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Cookie Policy</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Welcome to the cookie policy of JLS Apps. This policy will help you understand what cookies and tracking technologies we use, how we use them and what your rights are in relation to it.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              Latest update: March 03, 2025
            </p>
          </div>

          {/* Table of Contents */}
          <div className="mb-8 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Table of Contents</h2>
            <ul className="space-y-2">
              <li>
                <button 
                  onClick={() => scrollToSection('introduction')}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Introduction
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('owner')}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Owner and Data Controller
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('how-we-use')}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  How This Application Uses Trackers
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('third-party')}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Trackers Managed by Third Parties
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('manage-preferences')}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  How to Manage Preferences
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('definitions')}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Definitions
                </button>
              </li>
            </ul>
          </div>

          {/* Owner and Data Controller */}
          <div className="mb-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg" id="owner">
            <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">Owner and Data Controller</h2>
            <div className="text-gray-700 dark:text-gray-300">
              <p className="font-medium">JLS Trading Co</p>
              <p>5301 Terminal Street</p>
              <p>Charlotte NC 28208</p>
              <p className="mt-2">
                <span className="font-medium">Owner contact email:</span>{' '}
                <a 
                  href="mailto:rdhpndh@gmail.com" 
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  rdhpndh@gmail.com
                </a>
              </p>
            </div>
          </div>

          {/* Main Content */}
          <div className="space-y-8">
            {/* Introduction Section */}
            <section id="introduction">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Introduction</h2>
              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>
                  This document informs Users about the technologies that help this Application to achieve the purposes described below. 
                  Such technologies allow the Owner to access and store information (for example by using a Cookie) or use resources 
                  (for example by running a script) on a User's device as they interact with this Application.
                </p>
                <p>
                  For simplicity, all such technologies are defined as "Trackers" within this document – unless there is a reason to differentiate.
                  For example, while Cookies can be used on both web and mobile browsers, it would be inaccurate to talk about Cookies in the 
                  context of mobile apps as they are a browser-based Tracker. For this reason, within this document, the term Cookies is only 
                  used where it is specifically meant to indicate that particular type of Tracker.
                </p>
                <p>
                  Some of the purposes for which Trackers are used may also require the User's consent. Whenever consent is given, it can be 
                  freely withdrawn at any time following the instructions provided in this document.
                </p>
                <p>
                  This Application uses Trackers managed directly by the Owner (so-called "first-party" Trackers) and Trackers that enable 
                  services provided by a third-party (so-called "third-party" Trackers). Unless otherwise specified within this document, 
                  third-party providers may access the Trackers managed by them.
                </p>
                <p>
                  The validity and expiration periods of Cookies and other similar Trackers may vary depending on the lifetime set by the 
                  Owner or the relevant provider. Some of them expire upon termination of the User's browsing session.
                </p>
                <p>
                  In addition to what's specified in the descriptions within each of the categories below, Users may find more precise and 
                  updated information regarding lifetime specification as well as any other relevant information — such as the presence of 
                  other Trackers — in the linked privacy policies of the respective third-party providers or by contacting the Owner.
                </p>
              </div>
            </section>

            {/* How This Application Uses Trackers */}
            <section id="how-we-use">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">How This Application Uses Trackers</h2>
              
              <div className="space-y-6">
                <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                  <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Necessary</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    This Application uses so-called "technical" Cookies and other similar Trackers to carry out activities that are strictly 
                    necessary for the operation or delivery of the Service.
                  </p>
                </div>
                
                <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                  <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Functionality</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    This Application uses Trackers to enable basic interactions and functionalities, allowing Users to access selected features 
                    of the Service and facilitating the User's communication with the Owner.
                  </p>
                </div>
              </div>
            </section>

            {/* Trackers Managed by Third Parties */}
            <section id="third-party">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Trackers Managed by Third Parties</h2>
              
              <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Google LLC</h3>
                <div className="space-y-2 text-gray-700 dark:text-gray-300">
                  <p className="font-medium">Google OAuth</p>
                  <p>Company: Google LLC</p>
                  <p>Place of processing: United States</p>
                  <p>Personal Data processed: Trackers +2</p>
                </div>
              </div>
            </section>

            {/* How to Manage Preferences */}
            <section id="manage-preferences">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">How to Manage Preferences and Provide or Withdraw Consent</h2>
              
              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>
                  Whenever the use of Trackers is based on consent, users can provide or withdraw such consent by setting or updating their 
                  preferences via the relevant privacy choices panel available on this Application.
                </p>
                <p>
                  With regard to any third-party Trackers, Users can manage their preferences via the related opt-out link (where provided), 
                  by using the means indicated in the third party's privacy policy, or by contacting the third party.
                </p>
                
                <h3 className="text-xl font-semibold mt-6 mb-2 text-gray-900 dark:text-white">How to Control or Delete Cookies and Similar Technologies via Your Device Settings</h3>
                <p>
                  Users may use their own browser settings to:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>See what Cookies or other similar technologies have been set on the device;</li>
                  <li>Block Cookies or similar technologies;</li>
                  <li>Clear Cookies or similar technologies from the browser.</li>
                </ul>
                <p className="mt-2">
                  The browser settings, however, do not allow granular control of consent by category.
                </p>
                
                <p className="mt-4">
                  Users can, for example, find information about how to manage Cookies in the most commonly used browsers at the following addresses:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Google Chrome</li>
                  <li>Mozilla Firefox</li>
                  <li>Apple Safari</li>
                  <li>Microsoft Internet Explorer</li>
                  <li>Microsoft Edge</li>
                  <li>Brave</li>
                  <li>Opera</li>
                </ul>
                
                <p className="mt-4">
                  Users may also manage certain categories of Trackers used on mobile apps by opting out through relevant device settings such as 
                  the device advertising settings for mobile devices, or tracking settings in general (Users may open the device settings and look 
                  for the relevant setting).
                </p>
                
                <h3 className="text-xl font-semibold mt-6 mb-2 text-gray-900 dark:text-white">Consequences of Denying the Use of Trackers</h3>
                <p>
                  Users are free to decide whether or not to allow the use of Trackers. However, please note that Trackers help this Application 
                  to provide a better experience and advanced functionalities to Users (in line with the purposes outlined in this document). 
                  Therefore, if the User chooses to block the use of Trackers, the Owner may be unable to provide related features.
                </p>
              </div>
            </section>

            {/* Definitions Section */}
            <section id="definitions">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Definitions and Legal References</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Personal Data (or Data)</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Any information that directly, indirectly, or in connection with other information — including a personal identification number — allows for the 
                    identification or identifiability of a natural person.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Usage Data</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Information collected automatically through this Application (or third-party services employed in this Application), which can include: the IP addresses or 
                    domain names of the computers utilized by the Users who use this Application, the URI addresses (Uniform Resource Identifier), the time of the request, 
                    the method utilized to submit the request to the server, the size of the file received in response, the numerical code indicating the status of the server's 
                    answer (successful outcome, error, etc.), the country of origin, the features of the browser and the operating system utilized by the User, the various time 
                    details per visit (e.g., the time spent on each page within the Application) and the details about the path followed within the Application with special 
                    reference to the sequence of pages visited, and other parameters about the device operating system and/or the User's IT environment.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">User</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    The individual using this Application who, unless otherwise specified, coincides with the Data Subject.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Data Subject</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    The natural person to whom the Personal Data refers.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Data Processor (or Processor)</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    The natural or legal person, public authority, agency or other body which processes Personal Data on behalf of the Controller, as described in this privacy policy.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Data Controller (or Owner)</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    The natural or legal person, public authority, agency or other body which, alone or jointly with others, determines the purposes and means of the processing of 
                    Personal Data, including the security measures concerning the operation and use of this Application. The Data Controller, unless otherwise specified, is the 
                    Owner of this Application.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">This Application</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    The means by which the Personal Data of the User is collected and processed.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Service</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    The service provided by this Application as described in the relative terms (if available) and on this site/application.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">European Union (or EU)</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Unless otherwise specified, all references made within this document to the European Union include all current member states to the European Union and the 
                    European Economic Area.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Cookie</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Cookies are Trackers consisting of small sets of data stored in the User's browser.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Tracker</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Tracker indicates any technology - e.g Cookies, unique identifiers, web beacons, embedded scripts, e-tags and fingerprinting - that enables the tracking of 
                    Users, for example by accessing or storing information on the User's device.
                  </p>
                </div>
              </div>
              
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Legal Information</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  This privacy policy relates solely to this Application, if not stated otherwise within this document.
                </p>
              </div>
            </section>
          </div>

          {/* Footer */}
          <div className="mt-12 pt-6 border-t dark:border-gray-700">
            <div className="text-gray-700 dark:text-gray-300">
              <p className="font-medium">JLS Apps</p>
              <p>JLS Trading Co</p>
              <p>5301 Terminal Street</p>
              <p>Charlotte NC 28208</p>
              <p className="mt-2">
                <span className="font-medium">Owner contact email:</span>{' '}
                <a 
                  href="mailto:rdhpndh@gmail.com" 
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  rdhpndh@gmail.com
                </a>
              </p>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center mt-6">
              © {new Date().getFullYear()} JLS Trading Co. All rights reserved.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CookiePolicy;
